import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useAuth } from "../msalAuth/AuthProvider";
import { useEnv } from "./utils";
import collapse from "../../assets/chat/collapse-white-32.png";

export default function AIChatbox() {
  const { language } = useContext(LanguageContext);
  const { user } = useAuth();
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_AGENT_ID"]);

  const dfMessengerRef = useRef(null);
  const parameterRef = useRef(false);
  const languageRef = useRef(language || "th");
  // const autoDetectLanguageRef = useRef(true);
  const isInitializedRef = useRef(false);
  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "greeting" },
      },
    ],
    [language]
  );

  if (!isValid) {
    throw new Error(error);
  }

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const attachEmail = useCallback(() => {
    const email = user?.username;
    if (!email || !dfMessengerRef.current) return;

    dfMessengerRef.current.setQueryParameters({
      parameters: { email: email },
    });
    parameterRef.current = true;
  }, [user?.username]);

  const initializeChat = useCallback(
    (messenger) => {
      if (!messenger || isInitializedRef.current) return;

      messenger.startNewSession({ retainHistory: false });
      setTimeout(() => {
        attachEmail();
        messenger.sendRequest("event", "greeting");
        isInitializedRef.current = true;
      }, 500);
    },
    [attachEmail]
  );

  const clearQueryParameters = useCallback(() => {
    if (parameterRef.current && dfMessengerRef.current) {
      dfMessengerRef.current.setQueryParameters({});
      parameterRef.current = false;
    }
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }
  }, []);

  // const handleSentRequest = (event) => {
  //   const input = event.detail.data.requestBody.queryInput;
  //   let nextLanguageCode = currentLanguageRef.current;

  //   if ("text" in input) {
  //     const text = input.text.text;
  //     if (autoDetectLanguageRef.current) {
  //       const langCode = detectLanguage(text);
  //       input.languageCode = langCode;
  //       nextLanguageCode = langCode;
  //     } else {
  //       input.languageCode = currentLanguageRef.current;
  //     }
  //   } else {
  //     input.languageCode = currentLanguageRef.current;
  //   }

  //   if (nextLanguageCode !== currentLanguageRef.current) {
  //     currentLanguageRef.current = nextLanguageCode;
  //   }
  // };

  const handleChatSession = useCallback(
    (event) => {
      if (!event.detail.message || !dfMessengerRef.current) return;

      isInitializedRef.current = false;
      initializeChat(dfMessengerRef.current);
    },
    [initializeChat]
  );

  const handleSessionExpired = useCallback(() => {
    if (!dfMessengerRef.current) return;

    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const handleClickButton = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;
      const messenger = dfMessengerRef.current;

      const { event: evt, text } = event.detail;
      if (evt) {
        messenger.renderCustomText(text, false);
        if (evt === "greeting") {
          attachEmail();
          return;
        } else {
          clearQueryParameters();
          return;
        }
      }
      clearQueryParameters();
      if (text) {
        messenger.renderCustomText(text, false);
        messenger.sendRequest("query", text);
      } else {
        messenger.sendRequest("event", "flow-failed");
      }
    },
    [clearQueryParameters, attachEmail]
  );

  const renderTicket = useCallback((ticket, index) => {
    if (!dfMessengerRef.current) return;

    let { number, state, short_description, opened_date } = ticket;
    if (short_description === "-") {
      short_description = "Undefined";
    }

    const btn = [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: `${index + 1}) ${number} - ${opened_date} - ${state} - ${short_description}`,
      },
    ];

    dfMessengerRef.current.renderCustomCard(btn);
  }, []);

  const handleResponse = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;

      const messages = event.detail.data.messages;
      for (const message of messages) {
        if (message.type !== "text") continue;

        const actionRegex = /"action"\s*:\s*"([^"]+)"/;
        const match = message.text.match(actionRegex);

        if (!match || match[1] !== "followup_list_tickets") continue;

        event.preventDefault();

        try {
          const obj = JSON.parse(message.text);
          if (!obj.tickets || !obj.ticket_stat) continue;

          obj.tickets.forEach(renderTicket);
          dfMessengerRef.current.renderCustomText(obj.ticket_stat, true);
        } catch (error) {
          console.error("Error parsing ticket data:", error);
        }

        // autoDetectLanguageRef.current = true;
        // const currentPage = event.detail?.raw?.queryResult?.currentPage?.name || "";
        // if (currentPage) {
        //   const parts = currentPage.split("/");
        //   let currentFlowID = "";
        //   let currentPageID = "";

        //   for (let i = 0; i < parts.length; i++) {
        //     if (parts[i] === "flows" && i + 1 < parts.length) {
        //       currentFlowID = parts[i + 1];
        //     }
        //     if (parts[i] === "pages" && i + 1 < parts.length) {
        //       currentPageID = parts[i + 1];
        //     }
        //   }

        //   if (currentFlowID && currentPageID) {
        //     const flowData = pageData[currentFlowID] || "";
        //     if (flowData) {
        //       if (flowData["skip"].includes(currentPageID)) {
        //         autoDetectLanguageRef.current = false;
        //       }
        //     } else {
        //       autoDetectLanguageRef.current = false;
        //     }
        //   }
        // }
      }
    },
    [renderTicket]
  );

  useEffect(() => {
    const messenger = document.querySelector("df-messenger");
    const bubble = document.querySelector("df-messenger-chat-bubble");

    if (!messenger || !bubble) return;

    dfMessengerRef.current = messenger;
    bubble.openChat();

    const timeoutId = initializeChat(messenger);

    return () => {
      clearTimeout(timeoutId);
      isInitializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const eventHandlers = {
      "df-request-sent": handleSentRequest,
      "df-user-input-entered": clearQueryParameters,
      "df-chip-clicked": clearQueryParameters,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
      "df-genai-clear-chat-session": handleChatSession,
      "df-response-received": handleResponse,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [handleSentRequest, clearQueryParameters, handleSessionExpired, handleClickButton, handleChatSession, handleResponse]);

  return (
    <df-messenger project-id={values.DIALOGFLOW_CX_PROJECT_ID} agent-id={values.DIALOGFLOW_CX_AGENT_ID} allow-feedback="all" location="global">
      <df-messenger-chat-bubble
        chat-title="Sandee"
        allow-fullscreen="always"
        placeholder-text={getText("genai.placeholdertext")}
        chat-collapse-icon={collapse}
      />
    </df-messenger>
  );
}
